import { useState } from 'preact/hooks';
import {
  ALL_TIME,
  addMonths,
  cashFlow,
  lastMonths,
  slowStock,
  monthRange,
  monthlySales,
  periodReport,
  position,
  topItems,
  type DateRange,
} from '@khata/core';
import { Chips, Kpi, PageHead } from '../components/index.js';
import { compact, dShort, mShort, num, qty, taka } from '../lib/format.js';
import { count, t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { go } from '../lib/router.js';

type Period = 'this' | 'last' | 'all';

export function Reports() {
  const s = state.value;
  const m = master.value;
  const es = entries.value;
  const tday = today.value;
  const [period, setPeriod] = useState<Period>('this');
  const thisM = tday.slice(0, 7);
  const range: DateRange =
    period === 'all' ? ALL_TIME : monthRange(period === 'this' ? thisM : addMonths(thisM, -1));
  const rp = periodReport(s, es, range);
  const months = lastMonths(tday, 6);
  const chart = monthlySales(s, es, months);
  const max = Math.max(1, ...chart.map((c) => c.sales));
  const pos = position(s, m);
  const top = topItems(s, m, es, range, 6);
  const cf = cashFlow(s, m, es, range);
  const dead = slowStock(s, m, es, tday, 180);
  const label =
    period === 'all' ? t('period_all') : period === 'this' ? t('period_this') : t('period_last');

  return (
    <>
      <PageHead title={t('reports_title')} />
      <Chips
        options={[
          { key: 'this', label: t('reports_thisMonth') },
          { key: 'last', label: t('reports_lastMonth') },
          { key: 'all', label: t('reports_allTime') },
        ]}
        value={period}
        onChange={setPeriod}
      />
      <section class="kpis section-gap">
        <Kpi
          label={t('reports_sales')}
          value={taka(rp.sales)}
          sub={count(rp.salesCount, 'invoice', 'invoices')}
        />
        <Kpi label={t('reports_cogs')} value={taka(rp.cogs)} />
        <Kpi
          label={t('reports_gross')}
          value={taka(rp.grossProfit)}
          sub={rp.marginPct === null ? '—' : t('reports_margin', { pct: num(rp.marginPct) })}
          tone={rp.grossProfit < 0 ? 'bad' : 'ink'}
        />
        <Kpi label={t('reports_expenses')} value={taka(rp.expenses)} />
        <Kpi
          label={t('reports_net')}
          value={taka(rp.netProfit)}
          sub={t('reports_afterExpenses')}
          tone={rp.netProfit >= 0 ? 'good' : 'bad'}
        />
        <Kpi
          label={t('reports_purchases')}
          value={taka(rp.purchases)}
          sub={count(rp.purchaseCount, 'bill', 'bills')}
        />
        <Kpi label={t('reports_drawings')} value={taka(rp.drawings)} />
        <Kpi label={t('reports_returns')} value={taka(rp.salesReturns)} />
        {rp.stockAdjustments !== 0 && (
          <Kpi
            label={t('reports_stockAdj')}
            value={taka(rp.stockAdjustments)}
            sub={rp.stockAdjustments > 0 ? t('reports_loss') : t('reports_gain')}
          />
        )}
      </section>
      <h6 class="section">{t('reports_chart')}</h6>
      <div class="bars">
        {chart.map((c) => (
          <div key={c.month} class={'bar' + (c.month === thisM ? ' current' : '')}>
            <div>{c.sales ? compact(c.sales) : ''}</div>
            <div class="fill" style={{ height: Math.round((c.sales / max) * 100) + '%' }} />
            <div>{mShort(c.month)}</div>
          </div>
        ))}
      </div>
      <h6 class="section">
        {t('cf_title')} · {label}
      </h6>
      <section class="two-col section-gap" data-testid="cash-flow">
        <div>
          <div class="kv">
            <span>{t('cf_opening')}</span>
            <strong>{taka(cf.opening)}</strong>
          </div>
          <div class="kv" style={{ borderBottom: 0, paddingBottom: 0 }}>
            <span class="muted">{t('cf_in')}</span>
          </div>
          {(
            [
              ['cf_salesCash', cf.inflows.salesCash],
              ['cf_customerPayments', cf.inflows.customerPayments],
              ['cf_vendorRefunds', cf.inflows.vendorRefunds],
              ['cf_openingBalances', cf.inflows.openingBalances],
            ] as const
          )
            .filter(([, v]) => v !== 0)
            .map(([k, v]) => (
              <div key={k} class="kv">
                <span style={{ paddingLeft: 12 }}>{t(k)}</span>
                <span class="good">+{taka(v)}</span>
              </div>
            ))}
          <div class="kv" style={{ borderBottom: 0, paddingBottom: 0 }}>
            <span class="muted">{t('cf_out')}</span>
          </div>
          {(
            [
              ['cf_purchasesCash', cf.outflows.purchasesCash],
              ['cf_vendorPayments', cf.outflows.vendorPayments],
              ['cf_customerRefunds', cf.outflows.customerRefunds],
              ['cf_expenses', cf.outflows.expenses],
              ['cf_drawings', cf.outflows.drawings],
            ] as const
          )
            .filter(([, v]) => v !== 0)
            .map(([k, v]) => (
              <div key={k} class="kv">
                <span style={{ paddingLeft: 12 }}>{t(k)}</span>
                <span>−{taka(v)}</span>
              </div>
            ))}
          <div class="kv">
            <span>{t('cf_net')}</span>
            <strong class={cf.net < 0 ? 'bad' : 'good'}>{taka(cf.net)}</strong>
          </div>
          <div class="kv">
            <strong>{t('cf_closing')}</strong>
            <strong data-testid="cf-closing">{taka(cf.closing)}</strong>
          </div>
        </div>
        <div>
          <h6 class="section">{t('cf_byAccount')}</h6>
          <div style={{ overflowX: 'auto' }}>
            <table class="table">
              <thead>
                <tr>
                  <th>{t('cf_account')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cf_in_short')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cf_out_short')}</th>
                  <th style={{ textAlign: 'right' }}>{t('ledger_balance')}</th>
                </tr>
              </thead>
              <tbody>
                {cf.accounts.map((a) => (
                  <tr key={a.account.id}>
                    <td>{a.account.name}</td>
                    <td class="mono" style={{ textAlign: 'right' }}>
                      {a.inflow ? taka(a.inflow) : ''}
                    </td>
                    <td class="mono" style={{ textAlign: 'right' }}>
                      {a.outflow ? taka(a.outflow) : ''}
                    </td>
                    <td class="mono" style={{ textAlign: 'right' }}>
                      <strong>{taka(a.closing)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <h6 class="section">{t('reports_dead', { days: num(180) })}</h6>
      <section class="two-col section-gap" data-testid="dead-stock">
        <div>
          <div class="kv">
            <span>{t('reports_deadCount')}</span>
            <strong>{num(dead.count)}</strong>
          </div>
          <div class="kv">
            <span>{t('reports_deadValue')}</span>
            <strong class="bad">{taka(dead.value)}</strong>
          </div>
          <div class="kv">
            <span>{t('reports_neverSold')}</span>
            <strong>{num(dead.neverSold)}</strong>
          </div>
          {dead.count > 0 && (
            <div style={{ paddingTop: 12 }}>
              <button type="button" class="chip" onClick={() => go('stock', 'dead')}>
                {t('reports_seeInStock')}
              </button>
            </div>
          )}
        </div>
        <div>
          {dead.rows.slice(0, 8).map((r) => (
            <div key={r.item.id} class="kv">
              <span>
                {r.item.name}{' '}
                <span class="muted">
                  × {qty(r.qty)} ·{' '}
                  {r.lastSale
                    ? t('reports_lastSold', { date: dShort(r.lastSale) })
                    : t('reports_neverSoldShort')}
                </span>
              </span>
              <strong>{taka(r.value)}</strong>
            </div>
          ))}
        </div>
      </section>
      <section class="two-col">
        <div>
          <h6 class="section">{t('reports_position')}</h6>
          <div class="kv">
            <span>{t('reports_stockAtCost')}</span>
            <strong>{taka(pos.stockValue)}</strong>
          </div>
          {pos.defectiveValue > 0 && (
            <div class="kv">
              <span>{t('reports_defectiveAtCost')}</span>
              <strong>{taka(pos.defectiveValue)}</strong>
            </div>
          )}
          <div class="kv">
            <span>{t('reports_cash')}</span>
            <strong>{taka(pos.cash)}</strong>
          </div>
          <div class="kv">
            <span>{t('reports_bank')}</span>
            <strong>{taka(pos.bank)}</strong>
          </div>
          <div class="kv">
            <span>{t('reports_receivable')}</span>
            <strong class="bad">{taka(pos.receivable)}</strong>
          </div>
          <div class="kv">
            <span>{t('reports_payable')}</span>
            <strong>{taka(pos.payable)}</strong>
          </div>
          {(pos.customerAdvances > 0 || pos.vendorAdvances > 0) && (
            <div class="kv">
              <span>{t('reports_advances')}</span>
              <strong>
                {taka(pos.customerAdvances)} / {taka(pos.vendorAdvances)}
              </strong>
            </div>
          )}
          <div class="kv">
            <span>{t('reports_netWorth')}</span>
            <strong>{taka(pos.netWorth)}</strong>
          </div>
        </div>
        <div>
          <h6 class="section">{t('reports_top', { period: label })}</h6>
          {top.length === 0 && <div class="empty">{t('reports_noSales')}</div>}
          {top.map((ti) => (
            <div key={ti.item.id} class="kv">
              <span>
                {ti.item.name} <span class="muted">× {qty(ti.qty)}</span>
              </span>
              <strong>{taka(ti.amount)}</strong>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
